import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Crown, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { shareScoreCard } from "../lib/shareCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeaderboardModal({ open, onClose, currentScore }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sharing, setSharing] = useState(false);
    const myName = localStorage.getItem("ge-username") || "";
    const myBest = Math.max(Number(localStorage.getItem("ge-best") || 0), currentScore);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        fetch(`${API}/leaderboard`)
            .then((r) => r.json())
            .then((d) => setRows(Array.isArray(d) ? d : []))
            .catch(() => toast.error("LEADERBOARD OFFLINE — TRY AGAIN"))
            .finally(() => setLoading(false));
    }, [open]);

    const share = async () => {
        setSharing(true);
        try {
            await shareScoreCard(myBest, myName);
        } catch (e) {
            if (e?.name !== "AbortError") toast.error("SHARE FAILED — TRY AGAIN");
        } finally {
            setSharing(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    data-testid="leaderboard-modal"
                    onClick={onClose}
                    className="absolute inset-0 z-40 flex items-center justify-center bg-[#060608]/92 p-4"
                >
                    <motion.div
                        initial={{ scale: 0.92, y: 18 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.95, y: 10 }}
                        transition={{ type: "spring", stiffness: 300, damping: 26 }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md border-2 border-[#2A2A36] bg-[#0B0B0E] p-5 sm:p-7"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-pixel text-[11px] tracking-wider text-[#FFD60A] sm:text-sm">
                                    TODAY'S HIGH SCORES
                                </h3>
                                <p className="font-pixel mt-1.5 text-[6px] text-[#A1A1B2] sm:text-[7px]">
                                    BOARD RESETS DAILY — MIDNIGHT UTC
                                </p>
                            </div>
                            <button
                                data-testid="leaderboard-close-button"
                                onClick={onClose}
                                aria-label="Close leaderboard"
                                className="border border-[#2A2A36] p-1.5 text-[#A1A1B2] transition-colors hover:border-[#FF3B30] hover:text-[#FF3B30]"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div data-testid="leaderboard-list" className="mt-5 space-y-1.5">
                            {loading && (
                                <p className="font-pixel py-6 text-center text-[9px] text-[#A1A1B2]">
                                    LOADING…
                                </p>
                            )}
                            {!loading && rows.length === 0 && (
                                <p className="font-pixel py-6 text-center text-[9px] leading-relaxed text-[#A1A1B2]">
                                    NO SCORES YET.
                                    <br />
                                    BE THE FIRST ENFORCER.
                                </p>
                            )}
                            {!loading &&
                                rows.map((r, i) => (
                                    <div
                                        key={`${r.username}-${i}`}
                                        data-testid={`leaderboard-row-${i}`}
                                        className={`flex items-center gap-3 border px-3 py-2.5 ${
                                            r.username === myName
                                                ? "border-[#FFD60A] bg-[#FFD60A]/10"
                                                : "border-[#2A2A36] bg-[#121216]"
                                        }`}
                                    >
                                        <span className="font-pixel w-8 text-[9px] text-[#A1A1B2]">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        {i === 0 && (
                                            <Crown className="h-3.5 w-3.5 text-[#FFD60A]" />
                                        )}
                                        <span className="font-pixel flex-1 truncate text-[9px] text-[#F2F2F7]">
                                            {r.username}
                                        </span>
                                        <span className="font-pixel text-[9px] text-[#FFD60A]">
                                            {r.score}
                                        </span>
                                    </div>
                                ))}
                        </div>

                        <div className="mt-6 flex items-center justify-between gap-3 border-t border-[#2A2A36] pt-5">
                            <div>
                                <span className="font-pixel block text-[7px] text-[#A1A1B2]">
                                    YOUR BEST
                                </span>
                                <span
                                    data-testid="leaderboard-my-best"
                                    className="font-pixel text-sm text-[#38BDF8]"
                                >
                                    {myBest}
                                </span>
                            </div>
                            <button
                                data-testid="share-score-button"
                                onClick={share}
                                disabled={sharing || myBest === 0}
                                className="flex items-center gap-2.5 border-2 border-[#FF3B30] bg-[#FF3B30] px-4 py-3 font-pixel text-[8px] tracking-wider text-[#0A0A0C] transition-[background-color,color,transform] duration-150 hover:-translate-y-0.5 hover:bg-transparent hover:text-[#FF3B30] disabled:cursor-not-allowed disabled:opacity-40 sm:text-[9px]"
                            >
                                <Share2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                                {sharing ? "MAKING…" : "SHARE SCORE CARD"}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
